/**
 * A trigger said in words: "Payment Status is Paid and either Payment Mode is
 * Cash or UPI". Gmail rules say their When with it, OS says a task rule's
 * condition with it, and the phone and the web say an insight's.
 *
 * Every condition is spelled and nothing is cut. Each caller turns its own
 * shape into `TriggerSentenceCondition`s already in words; this file is the
 * only place that decides how those words are put together.
 *
 * The words were born as the Link sentence (Section 5.7) and kept their
 * `link.*` translation keys when the Link engine was removed (Phase 9 of
 * docs/plans/ledger.md).
 *
 * Pure: words come through `t`, figures through the shared formatters.
 */
import { formatMoney, formatNumber } from '../format';
import { formulaOf, isComputedType, type FieldSpec } from '../formula';
import { t } from '../i18n/t';
import type { TriggerConditionOp } from './trigger';

/** One condition, in words: the field asked about, the check, and each value already said. */
export type TriggerSentenceCondition = {
  /** "Payment Mode", or for a hop "the customer’s Credit allowed". */
  readonly field: string;
  readonly op: TriggerConditionOp;
  /** Each value in words: an option's label, a figure drawn, a name. */
  readonly values: readonly string[];
};

/** Every group must hold; inside a group any one is enough. */
export type TriggerSentenceGroups = readonly (readonly TriggerSentenceCondition[])[];

export type TriggerSentencePart =
  | { readonly kind: 'text'; readonly words: string }
  | { readonly kind: 'cond'; readonly group: number; readonly index: number; readonly words: string }
  | { readonly kind: 'conj'; readonly conj: 'and' | 'or' | 'either'; readonly words: string };

export const triggerValuesWords = (values: readonly string[]): string => {
  if (values.length <= 1) return values[0] ?? '';
  return `${values.slice(0, -1).join(', ')} ${t('link.or')} ${values.at(-1) ?? ''}`;
};

export const triggerConditionWords = (condition: TriggerSentenceCondition): string =>
  t(`link.clause.${condition.op}`, { field: condition.field, value: condition.values.length === 0 ? '…' : triggerValuesWords(condition.values) });

export const triggerConditionParts = (groups: TriggerSentenceGroups): TriggerSentencePart[] => {
  const parts: TriggerSentencePart[] = [];
  const kept = groups.map((group, at) => ({ group, at })).filter((one) => one.group.length > 0);
  kept.forEach(({ group, at }, place) => {
    if (place > 0) parts.push({ kind: 'text', words: ' ' }, { kind: 'conj', conj: 'and', words: t('link.and') }, { kind: 'text', words: ' ' });
    // "either" only where an "and" sits beside it: alone, "A or B" cannot be misread.
    if (group.length > 1 && kept.length > 1) parts.push({ kind: 'conj', conj: 'either', words: t('link.or_either') }, { kind: 'text', words: ' ' });
    group.forEach((condition, index) => {
      if (index > 0) parts.push({ kind: 'text', words: ' ' }, { kind: 'conj', conj: 'or', words: t('link.or') }, { kind: 'text', words: ' ' });
      parts.push({ kind: 'cond', group: at, index, words: triggerConditionWords(condition) });
    });
  });
  return parts;
};

export const triggerConditionsText = (groups: TriggerSentenceGroups): string =>
  triggerConditionParts(groups)
    .map((part) => part.words)
    .join('');

/* ---------------------------------------------------------------- figures */

/** What a figure needs of its field: its type, and a worked-out field's recipe. */
export type FigureField = { readonly type: string; readonly spec?: FieldSpec | null };

/** Whether a field holds money or a plain number, a worked-out field by what it works out to; null for anything else. */
const figureOf = (field: FigureField): 'money' | 'number' | null => {
  const type = isComputedType(field.type) ? formulaOf(field.spec)?.result : field.type;
  if (type === 'money') return 'money';
  return type === 'number' || type === 'quantity' ? 'number' : null;
};

/**
 * One value on a money or number field, drawn by the shared formatters whether
 * the rule stored it as a number or as its digits: "₹1", "1,50,000", never the
 * bare digits. Null for a field that holds no figure, or a value that is not one.
 */
export const figureWords = (field: FigureField, value: string | number, currency: string | null): string | null => {
  const figure = typeof value === 'number' ? value : value.trim() === '' ? Number.NaN : Number(value);
  if (!Number.isFinite(figure)) return null;
  const kind = figureOf(field);
  if (kind === 'money') return formatMoney(figure, currency);
  return kind === 'number' ? formatNumber(figure) : null;
};
